import {
  buildExchangeRequestFromShellContext,
  createAuthorizedFetcher,
  exchangeToken,
  type ModuleShellContext,
} from '@quantacredit/opshub-auth-browser';
import type { CampaignDeliveryRow, CampaignRow, Client, SegmentRow } from './types.js';

export type FrontendRuntimeConfig = {
  apiBaseUrl: string;
  opshubBaseUrl: string | null;
  authMode: 'none' | 'opshub';
  moduleKey: string;
};

export interface AccessTokenProvider {
  getAccessToken(): Promise<string | null>;
  setContext?(context: ModuleShellContext | null): void;
  authorizedFetch?(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly tokenProvider: AccessTokenProvider,
  ) {}

  async getClients(): Promise<Client[]> {
    return this.requestJson<Client[]>('/api/clients');
  }

  async getSegments(clientSlugs: string[]): Promise<SegmentRow[]> {
    return this.requestJson<SegmentRow[]>('/api/segments', {
      method: 'POST',
      body: JSON.stringify({ client_slugs: clientSlugs }),
    });
  }

  async getCampaignJourney(clientSlugs: string[], hours: number): Promise<CampaignRow[]> {
    return this.requestJson<CampaignRow[]>('/api/campaign-journey', {
      method: 'POST',
      body: JSON.stringify({ client_slugs: clientSlugs, hours }),
    });
  }

  async getCampaignDelivery(
    clientSlugs: string[],
    hours: number,
  ): Promise<CampaignDeliveryRow[]> {
    return this.requestJson<CampaignDeliveryRow[]>('/api/campaign-delivery', {
      method: 'POST',
      body: JSON.stringify({ client_slugs: clientSlugs, hours }),
    });
  }

  async clearServerCache(): Promise<{ cleared?: number }> {
    return this.requestJson<{ cleared?: number }>('/api/cache', { method: 'DELETE' });
  }

  private async requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body) {
      headers.set('Content-Type', 'application/json');
    }

    const response = this.tokenProvider.authorizedFetch
      ? await this.tokenProvider.authorizedFetch(this.resolveUrl(path), {
          ...init,
          headers,
        })
      : await this.requestWithOptionalBearer(this.resolveUrl(path), {
          ...init,
          headers,
        });

    if (!response.ok) {
      const errBody = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(errBody.error || response.statusText);
    }

    return (await response.json()) as T;
  }

  private async requestWithOptionalBearer(input: string, init: RequestInit): Promise<Response> {
    const headers = new Headers(init.headers);
    const token = await this.tokenProvider.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return fetch(input, {
      ...init,
      headers,
    });
  }

  private resolveUrl(path: string): string {
    return new URL(path, this.baseUrl).toString();
  }
}

export class NoopTokenProvider implements AccessTokenProvider {
  async getAccessToken(): Promise<string | null> {
    return null;
  }
}

export class OpsHubTokenProvider implements AccessTokenProvider {
  private context: ModuleShellContext | null = null;
  private cachedToken: string | null = null;
  private expiresAtMs = 0;
  private authorizedFetchImpl: ((input: RequestInfo | URL, init?: RequestInit) => Promise<Response>) | null = null;

  constructor(
    private readonly opshubBaseUrl: string,
    private readonly moduleKey: string,
  ) {}

  setContext(context: ModuleShellContext | null): void {
    const priorKey = this.contextSignature(this.context);
    const nextKey = this.contextSignature(context);
    this.context = context;
    if (priorKey !== nextKey) {
      this.cachedToken = null;
      this.expiresAtMs = 0;
      this.authorizedFetchImpl = null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    const context = this.context;
    if (!context) {
      throw new Error('OpsHub module context is required before loading report data');
    }
    if (context.module.key !== this.moduleKey) {
      throw new Error(
        `Unexpected OpsHub module key "${context.module.key}" (expected "${this.moduleKey}")`,
      );
    }

    const now = Date.now();
    if (this.cachedToken && now < this.expiresAtMs - 30_000) {
      return this.cachedToken;
    }

    if (!this.opshubBaseUrl) {
      throw new Error('OPSHUB_BASE_URL is required when authMode is "opshub"');
    }

    const payload = await exchangeToken({
      baseUrl: this.opshubBaseUrl,
      request: buildExchangeRequestFromShellContext(context),
    });
    this.cachedToken = payload.access_token;
    this.expiresAtMs = now + payload.expires_in * 1000;
    return this.cachedToken;
  }

  async authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!this.authorizedFetchImpl) {
      const context = this.context;
      if (!context) {
        throw new Error('OpsHub module context is required before loading report data');
      }
      this.authorizedFetchImpl = createAuthorizedFetcher({
        baseUrl: this.opshubBaseUrl,
        exchangeRequest: buildExchangeRequestFromShellContext(context),
        onTokenRefreshed: (payload) => {
          this.cachedToken = payload.access_token;
          this.expiresAtMs = Date.now() + payload.expires_in * 1000;
        },
      });
    }

    return this.authorizedFetchImpl(input, init);
  }

  private contextSignature(context: ModuleShellContext | null): string {
    return JSON.stringify({
      moduleKey: context?.module.key ?? null,
      tenantId: context?.tenant?.id ?? null,
      operatingClientKey: context?.tenant?.operating_client_key ?? null,
    });
  }
}

export async function loadRuntimeConfig(configUrl: string | URL): Promise<FrontendRuntimeConfig> {
  const response = await fetch(configUrl);
  if (!response.ok) {
    throw new Error(`Failed to load runtime config: ${response.status} ${response.statusText}`);
  }

  const payload = (await response.json()) as Partial<FrontendRuntimeConfig>;
  if (
    typeof payload.apiBaseUrl !== 'string' ||
    (payload.opshubBaseUrl !== null && payload.opshubBaseUrl !== undefined && typeof payload.opshubBaseUrl !== 'string') ||
    (payload.authMode !== 'none' && payload.authMode !== 'opshub') ||
    typeof payload.moduleKey !== 'string'
  ) {
    throw new Error('Runtime config response is invalid');
  }

  return {
    apiBaseUrl: payload.apiBaseUrl,
    opshubBaseUrl: payload.opshubBaseUrl ?? null,
    authMode: payload.authMode,
    moduleKey: payload.moduleKey,
  };
}
